import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell, BarChart,
  Area, PieChart, Pie,
} from 'recharts'

const PIE_COLORS = ['#2563eb', '#059669', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#eab308', '#6366f1', '#10b981', '#f97316', '#a855f7']

const fmt = (v: number) => {
  if (v == null) return '—'
  const a = Math.abs(v)
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}k`
  return v.toFixed(0)
}

export type VintageRow = { vintage: string; paid_in: number; distributed: number; residual: number; deal_count: number }

export function VintageChart({ data, ccy }: { data: VintageRow[]; ccy: string }) {
  return (
    <ResponsiveContainer width="100%" height={360}>
      <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey="vintage" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="l" tickFormatter={fmt} tick={{ fontSize: 12 }} label={{ value: ccy, angle: -90, position: 'insideLeft', fontSize: 12 }} />
        <YAxis yAxisId="r" orientation="right" allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip formatter={(v: number) => fmt(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="l" dataKey="paid_in" name="Paid-in" stackId="a" fill="#2563eb" />
        <Bar yAxisId="l" dataKey="distributed" name="Distributed" stackId="b" fill="#f59e0b" />
        <Bar yAxisId="l" dataKey="residual" name="Residual" stackId="b" fill="#fcd9a3" />
        <Line yAxisId="r" type="monotone" dataKey="deal_count" name="Deal count" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export type DispersionPoint = { company: string; tvpi: number | null; xirr: number | null; paid_in: number }

export function DispersionChart({ data }: { data: DispersionPoint[] }) {
  const pts = data
    .filter(d => d.tvpi != null && d.xirr != null)
    .map(d => ({ ...d, x: d.tvpi as number, y: (d.xirr as number) * 100, z: d.paid_in }))
  return (
    <ResponsiveContainer width="100%" height={420}>
      <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis type="number" dataKey="x" name="TVPI" unit="x" tick={{ fontSize: 12 }}
               label={{ value: 'TVPI', position: 'insideBottom', offset: -10, fontSize: 12 }} />
        <YAxis type="number" dataKey="y" name="XIRR" unit="%" tick={{ fontSize: 12 }}
               label={{ value: 'XIRR', angle: -90, position: 'insideLeft', fontSize: 12 }} />
        <ZAxis type="number" dataKey="z" range={[60, 600]} name="Paid-in" />
        <Tooltip cursor={{ strokeDasharray: '3 3' }}
                 formatter={(v: number, n: string) => n === 'Paid-in' ? fmt(v) : v.toFixed(2)}
                 labelFormatter={() => ''} content={<CustomTip />} />
        <Scatter data={pts}>
          {pts.map((p, i) => (
            <Cell key={i} fill={(p.x ?? 0) >= 1 ? '#34a37e' : '#e06666'} fillOpacity={0.75} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  )
}

function CustomTip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <div className="font-semibold">{p.company}</div>
      <div>TVPI {p.x?.toFixed(2)}x · XIRR {p.y?.toFixed(1)}%</div>
      <div>Paid-in {fmt(p.z)}</div>
    </div>
  )
}

export function JCurveChart({ data, ccy }: { data: { period: string; net: number; cumulative: number }[]; ccy: string }) {
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 12 }} label={{ value: `Cumulative net · ${ccy}`, angle: -90, position: 'insideLeft', fontSize: 11 }} />
        <Tooltip formatter={(v: number) => fmt(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Area type="monotone" dataKey="cumulative" name="Cumulative net CF" stroke="#2563eb" fill="#2563eb" fillOpacity={0.12} strokeWidth={2} />
        <Bar dataKey="net" name="Net CF (quarter)" fill="#94a3b8" />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export function SectorPie({ data }: { data: { group: string; total_value: number }[] }) {
  const pieData = data.filter(d => d.total_value > 0)
  return (
    <ResponsiveContainer width="100%" height={340}>
      <PieChart>
        <Pie data={pieData} dataKey="total_value" nameKey="group" cx="50%" cy="50%" outerRadius={120}
             label={(e: any) => e.group} labelLine={false}>
          {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v: number) => fmt(v)} />
      </PieChart>
    </ResponsiveContainer>
  )
}

export function CashflowChart({ data, ccy }: { data: { period: string; paid_in: number; distributed: number }[]; ccy: string }) {
  const shaped = data.map(d => ({ ...d, paid_in: -Math.abs(d.paid_in) }))
  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={shaped} margin={{ top: 10, right: 20, bottom: 10, left: 10 }} stackOffset="sign">
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 12 }} label={{ value: ccy, angle: -90, position: 'insideLeft', fontSize: 12 }} />
        <Tooltip formatter={(v: number) => fmt(Math.abs(v))} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="paid_in" name="Paid-in (outflow)" fill="#2563eb" stackId="s" />
        <Bar dataKey="distributed" name="Distributions (inflow)" fill="#059669" stackId="s" />
      </BarChart>
    </ResponsiveContainer>
  )
}
